import { Grid } from '@mui/material';
import { ofuscatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import { RoutalLoader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import PinField from 'react-pin-field';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import * as routes from '../../../constants/Routing';
import { useIsLogged } from '../../../hooks/session/useIsLogged';
import { useResendOTPLogin } from '../../../hooks/session/useResendOTPLogin';
import { useValidateSession } from '../../../hooks/session/useValidateSession';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import { AuthBody, AuthBodyFooter } from '../Auth.styled';
import { useUserAuthContext } from './context/context';
import { TwoFactorMethod } from './context/reducer';
import { useAuthStatus } from './hooks/useAuthStatus';

const RESEND_CODE_TIMEOUT_SECONDS = 30;

const ResendButton = ({ onClick }: { onClick: any }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [showCoundown, setShowCoundown] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(RESEND_CODE_TIMEOUT_SECONDS);

  useEffect(() => {
    if (!isVisible) {
      setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
  }, [isVisible, countdown]);

  useEffect(() => {
    if (countdown === 0) {
      setIsVisible(true);
    }
  }, [countdown]);

  return (
    <Grid container direction="column" alignItems="center">
      <Button
        style={{
          width: `calc(100% - 40px)`,
        }}
        disabled={!isVisible}
        onClick={() => {
          setShowCoundown(true);
          if (!isVisible) return;
          setIsVisible(false);
          setCountdown(RESEND_CODE_TIMEOUT_SECONDS);
          onClick();
        }}
        intlId="auth.resend_code"
      />
      {showCoundown && (
        <Text
          style={{
            fontSize: `13px`,
            color: RoutalPalette.secondary40,
            margin: 0,
            padding: 0,
            display: isVisible ? `none` : `block`,
            height: `fit-content`,
            fontWeight: 200,
          }}
        >
          {countdown}s
        </Text>
      )}
    </Grid>
  );
};

export const OTPForm = ({ twoFactorMethod }: { twoFactorMethod: TwoFactorMethod }) => {
  const [otp, setOtp] = useState<string>(``);
  const [searchParams] = useSearchParams();
  const { onSetAuthStep, reset, twoFactorMethods } = useAuthStatus();
  const { isLoading, success: validateSuccess, run: validate } = useValidateSession({ otpCode: otp });
  const navigate = useNavigate();
  const { isLogged } = useIsLogged();
  const { userAuthState } = useUserAuthContext();
  const { run: resendOTP } = useResendOTPLogin({ twoFactorMethod });

  const handlePaste: React.ClipboardEventHandler = (event) => {
    const data = event.clipboardData.getData(`text`);
    setOtp(data);
  };

  const onResendCode = () => {
    resendOTP();
  };

  const goBack = () => {
    if (twoFactorMethods.length > 1) {
      onSetAuthStep({ authStep: `methodSelector` });
    } else {
      reset();
    }
  };

  useEffect(() => {
    resendOTP();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (validateSuccess || isLogged) {
      const redirectTo = searchParams.get(`redirectTo`);
      if ((redirectTo ?? ``) !== ``) {
        // TODO: Control if URL is from highway??
        navigate(redirectTo as string);
      } else {
        navigate(routes.PRIVATE_HIGHWAY);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateSuccess, isLogged]);

  useEffect(() => {
    if (otp.length === 6) {
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <>
      <AuthBody container direction="column" justifyContent="center">
        <Grid item className="animate__animated animate__fadeIn">
          <Grid style={{ display: `flex`, justifyContent: `center`, alignItems: `center` }}>
            {twoFactorMethod === `email` && (
              <Text
                variant="h6"
                intlId="auth.login.2fa.emailOTP"
                intlValues={{ email: userAuthState.userIntent.email }}
              />
            )}
            {twoFactorMethod === `sms` && (
              <Text
                variant="h6"
                intlId="auth.login.2fa.smsOTP"
                intlValues={{ phone: ofuscatePhoneNumber(userAuthState.userIntent.phone!) }}
              />
            )}
          </Grid>
          <VSpacer large />
          <Grid item style={{ display: `flex`, justifyContent: `space-evenly` }}>
            {isLoading ? (
              <RoutalLoader />
            ) : (
              <StyledPinField
                className="pin-field"
                validate="0123456789"
                inputMode="numeric"
                onPaste={handlePaste}
                onChange={setOtp}
                autoComplete="one-time-code"
                length={6}
                autoFocus
                style={{}}
              />
            )}
          </Grid>

          <VSpacer large />

          <ResendButton onClick={() => onResendCode()} />

          <VSpacer large />
        </Grid>
      </AuthBody>

      <AuthBodyFooter item container justifyContent="center">
        <Grid item>
          <Text
            isBold
            style={{
              fontSize: `12px`,
            }}
            variant="button"
            intlId="back"
            onClick={goBack}
          />
        </Grid>
      </AuthBodyFooter>
    </>
  );
};

const StyledPinField = styled(PinField)`
  flex-direction: row;
  align-content: space-around;
  justify-content: center;
  flex-wrap: nowrap;
  width: 32px;
  height: 50px;
  font-size: 22px;
  font-weight: 600;
  color: ${RoutalPalette.secondary40};
  user-select: none;
  border-radius: 10px;
  border: 1px solid ${RoutalPalette.primary20};
  text-align: center;

  :focus {
    outline: none;
    box-shadow: inset ${RoutalPalette.primary40} 0px 0px 0px 2px !important;
    border-radius: 10px;
  }

  @media (max-width: 320px) {
    margin-right: 1px;
    margin-left: 1px;
    width: 27px;
  }
  @media (max-width: 270px) {
    margin-right: 1px;
    margin-left: 1px;
    width: 20px;
  }

  :nth-of-type(1) {
    margin-left: 0px;
  }
  :nth-last-of-type(1) {
    margin-right: 0px;
  }
`;
